import { isPresent } from "@mixitone/util";
import type { Assignment, FillContext, FillMode } from "./types";

const LAST_SET_PENALTY = 1000;

function pairKey(a: string, b: string): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

export function buildRepeatScorer(
  previousSets: Array<{ setIndex: number; games: string[][] }>,
  currentIndex: number,
): (playerIds: string[]) => number {
  const pairWeights = new Map<string, number>();

  for (const { setIndex, games } of previousSets) {
    const k = currentIndex - setIndex;
    const weight = 0.5 ** (k - 1);
    for (const gamePlayers of games) {
      for (let i = 0; i < gamePlayers.length; i++) {
        for (let j = i + 1; j < gamePlayers.length; j++) {
          const key = pairKey(gamePlayers[i], gamePlayers[j]);
          pairWeights.set(key, (pairWeights.get(key) ?? 0) + weight);
        }
      }
    }
  }

  return (playerIds: string[]) => {
    let score = 0;
    for (let i = 0; i < playerIds.length; i++) {
      for (let j = i + 1; j < playerIds.length; j++) {
        score += pairWeights.get(pairKey(playerIds[i], playerIds[j])) ?? 0;
      }
    }
    return score;
  };
}

export function scoreLevelBalanceForGame(
  playerIds: string[],
  playerLevels: Record<string, number | undefined>,
): number {
  const levels = playerIds
    .map((playerId) => playerLevels[playerId])
    .filter((level): level is number => isPresent(level));

  if (levels.length < 2) return 0;

  if (levels.length === 4) {
    const teamSplits: Array<[[number, number], [number, number]]> = [
      [
        [levels[0], levels[1]],
        [levels[2], levels[3]],
      ],
      [
        [levels[0], levels[2]],
        [levels[1], levels[3]],
      ],
      [
        [levels[0], levels[3]],
        [levels[1], levels[2]],
      ],
    ];
    return Math.min(
      ...teamSplits.map(([teamA, teamB]) => Math.abs(teamA[0] + teamA[1] - (teamB[0] + teamB[1]))),
    );
  }

  if (levels.length === 2) {
    return Math.abs(levels[0] - levels[1]);
  }

  return 0;
}

export function countUnitCombinations(units: string[][], n: number): number {
  const ways = new Array<number>(n + 1).fill(0);
  ways[0] = 1;
  for (const unit of units) {
    const size = unit.length;
    for (let total = n; total >= size; total--) {
      ways[total] += ways[total - size];
    }
  }
  return ways[n];
}

function scoreDoublesTeamBalance(layout: string[], playerLevels: Record<string, number | undefined>): number {
  const levels = layout
    .map((playerId) => playerLevels[playerId])
    .filter((level): level is number => isPresent(level));
  if (levels.length !== 4) return Number.POSITIVE_INFINITY;
  return Math.abs(levels[0] + levels[1] - (levels[2] + levels[3]));
}

export function bestDoublesOrder(
  playerIds: string[],
  playerLevels: Record<string, number | undefined>,
): string[] {
  if (playerIds.length !== 4) return playerIds;
  const allLevelled = playerIds.every((playerId) => isPresent(playerLevels[playerId]));
  if (!allLevelled) return playerIds;

  const layouts = [
    [playerIds[0], playerIds[1], playerIds[2], playerIds[3]],
    [playerIds[0], playerIds[2], playerIds[1], playerIds[3]],
    [playerIds[0], playerIds[3], playerIds[1], playerIds[2]],
  ];

  return layouts.reduce((best, layout) => {
    const score = scoreDoublesTeamBalance(layout, playerLevels);
    const bestScore = scoreDoublesTeamBalance(best, playerLevels);
    return score < bestScore ? layout : best;
  }, layouts[0]);
}

/** Lexicographic cost tuple, lower is better at each position in order */
export interface SetCost {
  /** Seats that could be filled by benched players but are not (prefers 4-player games in doubles_and_singles) */
  seatShortfall: number;
  /** Fairness: sum of timesOut over benched players, plus LAST_SET_PENALTY per benched satOutLastSet player */
  fairness: number;
  /** Sum across games of groups with an odd member count in that game */
  groupImbalance: number;
  /** Sum of scoreLevelBalanceForGame across games */
  level: number;
  /** Sum of repeatScore across games */
  repeat: number;
}

export function compareCost(a: SetCost, b: SetCost): number {
  if (a.seatShortfall !== b.seatShortfall) return a.seatShortfall - b.seatShortfall;
  if (a.fairness !== b.fairness) return a.fairness - b.fairness;
  if (a.groupImbalance !== b.groupImbalance) return a.groupImbalance - b.groupImbalance;
  if (a.level !== b.level) return a.level - b.level;
  return a.repeat - b.repeat;
}

const VALID_GAME_SIZES: Record<FillMode, Set<number>> = {
  doubles_only: new Set([0, 4]),
  singles_only: new Set([0, 2]),
  doubles_and_singles: new Set([0, 2, 4]),
};

function seatedPlayerIds(slots: Array<string | undefined>): string[] {
  return slots.filter((id): id is string => isPresent(id));
}

function gameSeatShortfall(mode: FillMode, seatedCount: number): number {
  if (seatedCount === 0) return 0;
  switch (mode) {
    case "singles_only":
      return seatedCount < 2 ? 2 - seatedCount : 0;
    case "doubles_only":
      return seatedCount < 4 ? 4 - seatedCount : 0;
    case "doubles_and_singles":
      if (seatedCount === 4) return 0;
      if (seatedCount === 2) return 2;
      if (seatedCount === 1 || seatedCount === 3) return 4 - seatedCount;
      return 0;
  }
}

export function gameGroupImbalance(ctx: FillContext, playerIds: string[]): number {
  const groupCounts = new Map<string, number>();
  for (const id of playerIds) {
    const groupId = ctx.playerGroups[id];
    if (!groupId) continue;
    groupCounts.set(groupId, (groupCounts.get(groupId) ?? 0) + 1);
  }
  let imbalance = 0;
  for (const count of groupCounts.values()) {
    if (count % 2 !== 0) imbalance += 1;
  }
  return imbalance;
}

/** Checks whether playerId's group is allowed alongside the groups of otherPlayerIds. */
export function canCoexist(ctx: FillContext, playerId: string, otherPlayerIds: string[]): boolean {
  const playerGroup = ctx.playerGroups[playerId];
  if (!playerGroup) return true;

  const otherGroups = [
    ...new Set(
      otherPlayerIds
        .filter((id) => id !== playerId)
        .map((id) => ctx.playerGroups[id])
        .filter(isPresent),
    ),
  ];
  const excludedGroups = new Set(otherGroups.flatMap((group) => ctx.groupExclusions[group] ?? []));
  return !excludedGroups.has(playerGroup);
}

function isSolverPlaced(fixed: Array<string | undefined>, playerId: string): boolean {
  return !fixed.includes(playerId);
}

function isFixedPlayer(ctx: FillContext, playerId: string): boolean {
  return ctx.games.some((game) => game.fixed.includes(playerId));
}

export function isValidAssignment(ctx: FillContext, assignment: Assignment): boolean {
  const contextGames = new Map(ctx.games.map((game) => [game.gameIndex, game]));
  const playerGame = new Map<string, number>();
  const benched = new Set(assignment.bench);
  const activePlayerIds = new Set(ctx.playerIds);

  for (const game of assignment.games) {
    const contextGame = contextGames.get(game.gameIndex);
    if (!contextGame) return false;

    if (game.slots.length !== 4) return false;

    const fixed = contextGame.fixed;
    const players = seatedPlayerIds(game.slots);
    const seatedCount = players.length;
    const solverPlacedCount = players.filter((id) => isSolverPlaced(fixed, id)).length;

    if (solverPlacedCount > 0 && !VALID_GAME_SIZES[ctx.mode].has(seatedCount)) return false;

    for (let slot = 0; slot < 4; slot++) {
      const fixedPlayer = fixed[slot];
      const assignedPlayer = game.slots[slot];
      if (isPresent(fixedPlayer) && assignedPlayer !== fixedPlayer) return false;
    }

    for (const playerId of players) {
      if (playerGame.has(playerId)) return false;
      playerGame.set(playerId, game.gameIndex);

      const isFixed = fixed.includes(playerId);
      if (!isFixed && !activePlayerIds.has(playerId)) return false;

      if (isSolverPlaced(fixed, playerId)) {
        const others = players.filter((id) => id !== playerId);
        if (!canCoexist(ctx, playerId, others)) return false;
      }
    }
  }

  for (const playerId of assignment.bench) {
    if (playerGame.has(playerId)) return false;
    if (!activePlayerIds.has(playerId)) return false;
  }

  for (const playerId of ctx.playerIds) {
    if (!playerGame.has(playerId) && !benched.has(playerId)) return false;
  }

  const seenPairs = new Set<string>();
  for (const [playerId, partnerId] of Object.entries(ctx.playerPairs)) {
    const key = pairKey(playerId, partnerId);
    if (seenPairs.has(key)) continue;
    seenPairs.add(key);

    const playerGameIndex = playerGame.get(playerId);
    const partnerGameIndex = playerGame.get(partnerId);
    if (isFixedPlayer(ctx, playerId) || isFixedPlayer(ctx, partnerId)) continue;

    const playerBenched = benched.has(playerId);
    const partnerBenched = benched.has(partnerId);

    if (playerBenched !== partnerBenched) return false;
    if (
      playerGameIndex !== undefined &&
      partnerGameIndex !== undefined &&
      playerGameIndex !== partnerGameIndex
    ) {
      return false;
    }
  }

  return true;
}

export type RepeatScorer = (playerIds: string[]) => number;

export function assignmentCost(
  ctx: FillContext,
  assignment: Assignment,
  repeatScore: RepeatScorer = buildRepeatScorer(ctx.previousSets, ctx.currentSetIndex),
): SetCost {
  const satOutLastSet = new Set(ctx.satOutLastSet);
  let seatShortfall = 0;
  let groupImbalance = 0;
  let level = 0;
  let repeat = 0;

  for (const game of assignment.games) {
    const players = seatedPlayerIds(game.slots);
    seatShortfall += gameSeatShortfall(ctx.mode, players.length);
    groupImbalance += gameGroupImbalance(ctx, players);
    level += scoreLevelBalanceForGame(players, ctx.playerLevels);
    repeat += repeatScore(players);
  }

  let fairness = 0;
  for (const playerId of assignment.bench) {
    fairness += ctx.timesOut[playerId] ?? 0;
    if (satOutLastSet.has(playerId)) fairness += LAST_SET_PENALTY;
  }

  return { seatShortfall, fairness, groupImbalance, level, repeat };
}
